import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Link2, Plus, Filter, Trash2, X, Search } from 'lucide-react'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useIsMobile } from '../hooks/useIsMobile'
import { useQuotes } from '../hooks/useQuotes'
import { navState } from '../crm/services/pageCache'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import Pagination from '../components/Pagination'
import EngagementDot from '../components/EngagementDot'
import EngagementLegend from '../components/EngagementLegend'
import { colors, borderRadius } from '../styles/tokens'

const SELLERS = [
  { id: null, name: 'Todos' },
  { id: 'diogenes', name: 'Diógenes' },
  { id: 'annaluiza', name: 'Anna Luiza' },
  { id: 'efiso', name: 'Efiso Brito' },
]

// ─── Modal de Agrupamento ─────────────────────────────────────────────────────

// ─── Dashboard Principal ──────────────────────────────────────────────────────

export default function SellerDashboard() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const sid = profile?.seller_id || null

  const [page, setPage] = useState(() => navState.get(sid, 'cotacoes', 'page') || 1)
  const [filterSellerId, setFilterSellerId] = useState(() => navState.get(sid, 'cotacoes', 'filterSellerId') || null)

  const userName = profile?.name || 'Usuário'
  const isAdmin = profile?.is_admin === true
  const isMobile = useIsMobile()

  const { quotes, total, totalPages, hasNext, hasPrev, isLoading, refresh } = useQuotes(filterSellerId, page)

  // ─── Engagement ───
  const [engagements, setEngagements] = useState({})

  React.useEffect(() => {
    if (quotes.length > 0) {
      const ids = quotes.map(q => q.id)
      api.getEngagementBulk(ids)
        .then(data => setEngagements(data))
        .catch(() => {})
    }
  }, [quotes])

  // ─── Agrupamento removido — pipeline opera por contact_id ───

  // ─── Handlers existentes ───
  const handleFilterChange = (e) => {
    const value = e.target.value === '' ? null : e.target.value
    setFilterSellerId(value)
    setPage(1)
    navState.set(sid, 'cotacoes', 'filterSellerId', value)
    navState.set(sid, 'cotacoes', 'page', 1)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    navState.set(sid, 'cotacoes', 'page', newPage)
  }

  const handleDelete = async (e, quoteId, clientName) => {
    e.stopPropagation()
    if (!window.confirm(`Tem certeza que deseja excluir a cotação de "${clientName}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }
    try {
      const result = await api.deleteQuote(quoteId)
      if (result.success) {
        refresh()
      } else {
        alert(result.error || 'Erro ao excluir cotação')
      }
    } catch (error) {
      alert('Erro ao excluir cotação: ' + error.message)
    }
  }

  const handleCopyLink = (e, publicLink) => {
    e.stopPropagation()
    navigator.clipboard.writeText(`${window.location.origin}/c/${publicLink}`)
    alert('Link copiado!')
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-BR')
  }

  const getSellerName = (sellerId) => {
    const seller = SELLERS.find(s => s.id === sellerId)
    return seller?.name || sellerId || 'Não identificado'
  }

  const statusLabels = { draft: 'Rascunho', published: 'Ativa', updated: 'Atualizada' }

  const getStatusStyle = (status) => ({
    padding: '0.25rem 0.75rem',
    borderRadius: borderRadius.full,
    fontSize: '0.75rem',
    fontWeight: 500,
    backgroundColor: status === 'draft' ? colors.status.expired : colors.status.active,
    color: status === 'draft' ? colors.text.secondary : colors.white
  })

  const buttonOutline = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
    color: colors.primary, backgroundColor: 'transparent',
    border: `1px solid ${colors.primary}`, borderRadius: borderRadius.sm, cursor: 'pointer'
  }

  const buttonPrimary = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
    color: colors.white, backgroundColor: colors.accent,
    border: 'none', borderRadius: borderRadius.sm, cursor: 'pointer'
  }

  const buttonDanger = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
    color: '#dc2626', backgroundColor: 'transparent',
    border: '1px solid #dc2626', borderRadius: borderRadius.sm, cursor: 'pointer'
  }

  const selectStyle = {
    padding: '0.5rem 1rem', fontSize: '0.875rem',
    borderRadius: borderRadius.sm, border: `1px solid ${colors.border}`,
    backgroundColor: colors.card, color: colors.text.primary,
    cursor: 'pointer', minWidth: '150px'
  }

  const showQuotes = quotes.length > 0

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background, fontFamily: "'Inter', sans-serif" }}>
      <Header subtitle="Cotações" userName={userName} />

      <div style={{ display: 'flex', height: isMobile ? 'auto' : 'calc(100vh - 73px)' }}>
        <Sidebar activePage="cotacoes" />

        <main style={{ flex: 1, padding: isMobile ? '1rem' : '2rem 1.5rem', overflowY: 'auto', paddingBottom: isMobile ? '80px' : undefined }}>
          <div style={{
            backgroundColor: colors.card, borderRadius: borderRadius.lg,
            border: `1px solid ${colors.border}`, padding: isMobile ? '1rem' : '2rem',
            maxWidth: '1280px', margin: '0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 600, color: colors.primary, margin: 0 }}>
                Cotações
              </h2>
            </div>

            {/* Filtro */}
            <div style={{
              display: 'flex', alignItems: isMobile ? 'stretch' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '0.75rem', marginBottom: '1.5rem',
              paddingBottom: '1rem', borderBottom: `1px solid ${colors.border}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Filter size={18} color={colors.text.secondary} />
                {!isMobile && (
                  <label style={{ fontSize: '0.875rem', color: colors.text.secondary }}>
                    Filtrar por vendedor:
                  </label>
                )}
                <select value={filterSellerId || ''} onChange={handleFilterChange} style={selectStyle}>
                  {SELLERS.map(seller => (
                    <option key={seller.id || 'all'} value={seller.id || ''}>
                      {seller.name}
                    </option>
                  ))}
                </select>
              </div>
              {total > 0 && (
                <span style={{ fontSize: '0.875rem', color: colors.text.secondary, marginLeft: 'auto' }}>
                  {total} cotação(ões)
                </span>
              )}
              <EngagementLegend />
            </div>

            {isLoading && !showQuotes ? (
              <p style={{ color: colors.text.secondary }}>Carregando...</p>
            ) : !showQuotes ? (
              <p style={{ color: colors.text.secondary }}>Nenhuma cotação encontrada.</p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {quotes.map(quote => (
                    <div
                      key={quote.id}
                      onClick={isMobile ? () => navigate(`/app/cotacao/${quote.id}`) : undefined}
                      style={{
                        display: 'flex',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        padding: isMobile ? '1rem' : '1.25rem',
                        backgroundColor: colors.card,
                        border: `1px solid ${colors.border}`,
                        borderRadius: borderRadius.md,
                        transition: 'all 0.2s',
                        cursor: isMobile ? 'pointer' : 'default',
                        gap: isMobile ? '0.75rem' : 0
                      }}
                    >
                      <div style={{ flex: 1, width: isMobile ? '100%' : undefined }}>
                        <h3 style={{
                          fontSize: '1rem', fontWeight: 500, color: colors.text.primary,
                          marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <EngagementDot engagement={engagements[quote.id]} />
                          {quote.contact_id ? (
                            <span
                              onClick={(e) => { e.stopPropagation(); navigate(`/app/conversas?contact_id=${quote.contact_id}`) }}
                              style={{ cursor: 'pointer', color: colors.accent, textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                              title="Abrir conversa no CRM"
                            >
                              {quote.client_name}
                            </span>
                          ) : quote.client_name}
                          {quote.destination && (
                            <span style={{ fontWeight: 400, color: colors.text.secondary }}>
                              {' '}— {quote.destination}
                            </span>
                          )}
                        </h3>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          fontSize: '0.875rem', color: colors.text.secondary, flexWrap: 'wrap'
                        }}>
                          <span>Criada em {formatDate(quote.created_at)}</span>
                          <span style={{ width: '4px', height: '4px', backgroundColor: colors.border, borderRadius: '50%' }}></span>
                          <span>{getSellerName(quote.seller_id)}</span>
                          <span style={{ width: '4px', height: '4px', backgroundColor: colors.border, borderRadius: '50%' }}></span>
                          <span style={getStatusStyle(quote.status)}>{statusLabels[quote.status]}</span>
                        </div>
                        {isMobile && quote.final_price > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '1.1rem', fontWeight: 700, color: colors.accent }}>
                            R$ {quote.final_price.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                          </div>
                        )}
                      </div>

                      {!isMobile && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <button
                            style={buttonOutline}
                            onClick={() => navigate(`/app/cotacao/${quote.id}`)}
                          >
                            <Eye size={16} />
                            Ver cotação
                          </button>
                          {quote.status !== 'draft' && (
                            <button
                              style={buttonPrimary}
                              onClick={(e) => handleCopyLink(e, quote.public_link)}
                            >
                              <Link2 size={16} />
                              Copiar link
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              style={buttonDanger}
                              onClick={(e) => handleDelete(e, quote.id, quote.client_name)}
                              title="Excluir cotação"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </div>
        </main>
      </div>

      {/* fim do dashboard */}
    </div>
  )
}
